import { Route, Routes, useLocation, BrowserRouter, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';
import Layout from '@layout/Layout';
import RouteTransition from '@layout/RouteTransition';
import WelcomeScreen from '@pages/Welcome';
import {
  IdleWarningModal,
  ProtectedRoute,
  RememberingRedirect,
  ReturnToRedirect,
} from '@components/auth';
import { AuthProvider, useAuth } from '@contexts/auth/AuthContext';
import Loading from '@components/ui/Loading';
import { ProfileProvider, useProfile } from '@contexts/profile/ProfileContext';
import { BusinessProvider } from '@contexts/business/BusinessContext';
import { DashboardProvider } from '@contexts/DashboardContext';
import { CoreLayoutProvider, AuthLayoutProvider } from '@contexts/layout';
import Withdrawal from '@pages/Withdrawal';
import { MANAGE_AUTO_ROLL_PATH } from '@/constants/autoRoll';
import { LOGO_ONLY_ROUTES } from '@/constants/layoutRoutes';

const Dashboard = React.lazy(() => import('@pages/Dashboard'));
const ManageAssets = React.lazy(() => import('@pages/ManageAssets'));
const PurchaseAssets = React.lazy(() => import('@pages/Purchase'));
const BusinessSelect = React.lazy(() => import('@pages/BusinessSelect'));
const TreasuryBillPurchase = React.lazy(() => import('@pages/TreasuryBillPurchase'));
const ManageTreasuryBill = React.lazy(() => import('@pages/ManageTreasuryBill'));
const ManageAutoRoll = React.lazy(() => import('@pages/ManageAutoRoll'));
const Wallet = React.lazy(() => import('@pages/Wallet'));
const AddMoney = React.lazy(() => import('@pages/AddMoney'));
const Transactions = React.lazy(() => import('@pages/Transactions'));
const Reports = React.lazy(() => import('@pages/Reports'));
const LoginPage = React.lazy(() => import('@pages/Login'));
const RegisterPage = React.lazy(() => import('@pages/Register'));
const ResetPasswordPage = React.lazy(() => import('@pages/ResetPassword'));
const ForgotPasswordPage = React.lazy(() => import('@pages/ForgotPassword'));

const getPageTitle = (pathname: string): string => {
  switch (pathname) {
    case '/dashboard':
      return 'Dashboard';
    case '/manage':
      return 'Manage Assets';
    case MANAGE_AUTO_ROLL_PATH:
      return 'Auto-roll';
    case '/purchase':
      return 'Purchase Assets';
    case '/wallet':
      return 'Wallet';
    case '/wallet/transactions':
      return 'Transactions';
    case '/reports':
      return 'Reports';
    case '/welcome':
      return 'Welcome';
    case '/business-select':
      return 'Select Business';
    default:
      return 'Dashboard';
  }
};

// Welcome and business-select hand over to the stored path themselves; only the dashboard
// default skips them, so it is the one that must honour it.
const DefaultRedirect: React.FC<{ to: string }> = ({ to }) =>
  to === '/dashboard' ? <ReturnToRedirect fallback={to} /> : <Navigate to={to} replace />;

const AppContent: React.FC = () => {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: authLoading,
    idleWarningActive,
    dismissIdleWarning,
    logout,
  } = useAuth();
  const { profileData, loading: _profileLoading, currentBusinessId, clearAndLogout } = useProfile();
  const title = getPageTitle(location.pathname);

  const accountStatus = profileData?.status || null;

  // Defensive account-status gate.
  //
  // Account status (numeric, from profileData.status): 0 = PendingApproval,
  // 1 = Active, 2 = Rejected (3 = Deleted). Under the "no JWT for Pending
  // users" anti-enumeration decision, a PendingApproval user never receives a
  // token, so an authenticated session can only ever be Active. Any other
  // status reaching this gate is anomalous — force a sign-out rather than
  // routing anywhere. The post-submit "application in review" moment is owned
  // entirely by the registration wizard's in-component `pending` stage and
  // never depends on an authenticated route.
  const isNonActiveStatus = accountStatus !== null && accountStatus !== 1; // 0 (Pending, defensive), 2 (Rejected), 3 (Deleted)

  React.useEffect(() => {
    if (isAuthenticated && isNonActiveStatus) {
      void clearAndLogout();
    }
  }, [isAuthenticated, isNonActiveStatus, clearAndLogout]);

  // Default route logic - welcome only on first login, dashboard if business selected
  const getDefaultRoute = () => {
    if (isAuthenticated) {
      // Check if user has a saved business selection that will be restored
      const savedBusinessId = sessionStorage.getItem('selectedBusinessId');
      const hasValidSavedBusiness =
        savedBusinessId && profileData?.corporateEntities?.find((ce) => ce.id === savedBusinessId);

      // If user has a business selected OR will have it restored, always go to dashboard
      if (currentBusinessId || hasValidSavedBusiness) {
        return '/dashboard';
      }

      // Check if welcome has been shown this session
      const welcomeShown = sessionStorage.getItem('welcome-shown');
      if (welcomeShown) {
        // Welcome already shown, skip to appropriate page based on available businesses
        if (profileData?.corporateEntities && profileData.corporateEntities.length > 1) {
          return '/business-select';
        } else if (profileData?.corporateEntities && profileData.corporateEntities.length === 1) {
          return '/dashboard';
        } else {
          // No businesses available — send to the business-select screen
          return '/business-select';
        }
      }

      // First time this session, show welcome
      return '/welcome';
    }
    return '/login';
  };

  // Show loading while authentication is being determined
  if (authLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return (
      <Layout title="Login">
        <Routes>
          <Route path="*" element={<RememberingRedirect to="/login" />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Routes>
      </Layout>
    );
  }

  // Non-active account status (Pending/Rejected/Deleted): the effect above is
  // forcing a sign-out. Render nothing routable in the meantime — never route
  // a non-active session into the authenticated app tree.
  if (isAuthenticated && isNonActiveStatus) {
    return <Loading />;
  }

  // Handle case where profile hasn't loaded yet but user is authenticated
  if (isAuthenticated && !profileData) {
    return <Loading />; // Still loading profile data
  }

  // Early redirect logic - only redirect if user needs welcome/business selection
  // This prevents layout from rendering and animating before redirect
  const shouldRedirectToWelcomeFlow = () => {
    if (!isAuthenticated || !profileData) return false;

    // Check if user has a saved business selection that will be restored
    const savedBusinessId = sessionStorage.getItem('selectedBusinessId');
    const hasValidSavedBusiness =
      savedBusinessId && profileData.corporateEntities?.find((ce) => ce.id === savedBusinessId);

    // If user has business selected OR will have it restored, no redirect needed
    if (currentBusinessId || hasValidSavedBusiness) return false;

    // If already on welcome/business-select pages, no redirect needed
    if (['/welcome', '/business-select'].includes(location.pathname)) return false;

    // Check if welcome has been shown this session
    const welcomeShown = sessionStorage.getItem('welcome-shown');

    // If welcome not shown yet, redirect to welcome
    if (!welcomeShown) {
      return '/welcome';
    }

    // Welcome already shown, check if user needs business selection.
    // Users with no businesses also land on business-select (handles the
    // empty-list case) — there is no separate pending screen.
    if (
      profileData.corporateEntities &&
      (profileData.corporateEntities.length > 1 || profileData.corporateEntities.length === 0)
    ) {
      return '/business-select';
    }

    // No redirect needed
    return false;
  };

  const redirectTarget = shouldRedirectToWelcomeFlow();
  if (redirectTarget) {
    return <RememberingRedirect to={redirectTarget} />;
  }

  // Determine if current route should use Layout - now handled by LayoutProvider
  // const shouldUseLayout = !["/welcome", "/business-select"].includes(location.pathname);

  const routeContent = (
    <RouteTransition>
      <Suspense fallback={<Loading />}>
        <Routes location={location} key={location.pathname}>
          <Route element={<ProtectedRoute />}>
            <Route path="/welcome" element={<WelcomeScreen />} />
            <Route path="/business-select" element={<BusinessSelect />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/manage" element={<ManageAssets />} />
            <Route path="/manage/treasury-bill/:id" element={<ManageTreasuryBill />} />
            <Route path={MANAGE_AUTO_ROLL_PATH} element={<ManageAutoRoll />} />
            <Route path="/purchase" element={<PurchaseAssets />} />
            <Route path="/purchase/treasurybill" element={<TreasuryBillPurchase />} />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/wallet/add-money" element={<AddMoney />} />
            <Route
              path="/wallet/withdraw"
              element={
                <Withdrawal
                  onBackClick={function (): void {
                    throw new Error('Function not implemented.');
                  }}
                  onCancel={function (): void {
                    throw new Error('Function not implemented.');
                  }}
                  onBackToManageAssets={function (): void {
                    throw new Error('Function not implemented.');
                  }}
                />
              }
            />
            <Route path="/wallet/transactions" element={<Transactions />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="*" element={<DefaultRedirect to={getDefaultRoute()} />} />
          </Route>
        </Routes>
      </Suspense>
    </RouteTransition>
  );

  // Always render with Layout - LayoutProvider will handle visibility
  return (
    <>
      <Layout title={title}>{routeContent}</Layout>
      <IdleWarningModal
        isOpen={idleWarningActive}
        countdownSeconds={60}
        onDismiss={dismissIdleWarning}
        onLogout={() => {
          void logout();
        }}
      />
    </>
  );
};
const App: React.FC = () => {
  return (
    <BrowserRouter>
      {/* Core layout - always active, no auth dependencies */}
      <CoreLayoutProvider
        hiddenLogoRoutes={['/welcome']}
        gradientRoutes={[
          '/dashboard',
          '/wallet',
          '/manage',
          '/purchase',
          '/reports',
          '/wallet/transactions',
          '/login',
        ]}
      >
        {/* Auth providers */}
        <AuthProvider>
          <ProfileProvider>
            <BusinessProvider>
              <DashboardProvider>
                {/* Auth-dependent layout - only when needed */}
                <AuthLayoutProvider
                  hiddenNavbarRoutes={LOGO_ONLY_ROUTES}
                  hiddenHeaderRoutes={LOGO_ONLY_ROUTES}
                >
                  <AppContent />
                </AuthLayoutProvider>
              </DashboardProvider>
            </BusinessProvider>
          </ProfileProvider>
        </AuthProvider>
      </CoreLayoutProvider>
    </BrowserRouter>
  );
};

export default App;
